
/**
 * Callout scripts.
 *
 * Handles animations and changing the bottom background color of callouts.
 */

/** Dependencies -------------------------------------------- */

// External

import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import ScrollSmoother from 'gsap/ScrollSmoother'

gsap.registerPlugin( ScrollTrigger, ScrollSmoother )

// Design system

import { getCssVar } from '../helpers'
import { deriveBackgroundColorContext } from '../blocks/helpers'

/** The beef ------------------------------------------------ */

/**
 * Callout mixin.
 * Provides callout functionality to single element.
 */
export let mixin = ( { node = null, config = {} } = {} ) => {

    let state = {
        node,
        config,
        sibling    : null,
        container  : null,
        card       : null,
        timeline   : null
    };

    let module = {

        init() {

            state.container = state.node.querySelector( `.${state.config.className}__container` )
            state.card = state.node.querySelector( `.${state.config.className}__card` )

            // Get the adjoining element to determine what the bottom background color should be.
            // This is either going to be a section or the footer.
            // When it's the footer, we have to back the query out and then continue, since the footer is outside of the main element.

            state.sibling = state.node.nextElementSibling || state.node.parentElement.parentElement.parentElement.nextElementSibling

            // And when that doesn't work, something is probably wrong and we bail

            if ( ! state.sibling ) return

            const backgroundColorContext = deriveBackgroundColorContext( state.sibling )

            if ( ! backgroundColorContext ) return

            state.node.classList.add( `has-bottom-background-color-${backgroundColorContext.color}` )

            gsap.set( state.node, { '--calloutPageFoldSize': 0 } )
            gsap.set( state.container, { transformOrigin: 'top center', scale: 1.15 } )
            gsap.set( state.card, { opacity: 0 } )

            this.animate()
        },

        animate() {

            ScrollSmoother.get().effects(
                state.container,
                { speed: state.config.parallaxSpeed, lag: state.config.parallaxLag }
            )

            state.timeline = gsap.timeline( { paused: true } )
                .to( state.container, { scale: 1 }, '<' )
                .to( state.card, { opacity: 1 }, '<' )
                .to( state.node, { '--calloutPageFoldSize': () => getCssVar( 'calloutFullPageFoldSize', state.node ) }, '<' )

            ScrollTrigger.create({
                animation : state.timeline,
                trigger   : state.node,
                start     : 'top bottom',
                end       : 'center center',
                scrub     : true
            })

        }
    };

    return module.init()
};

/**
 * Callout module.
 * Provides an interface to apply callout functionality to all elements matching a selector.
 */
export default {

    init( { className, config = {} } ) {

        let selected = document.querySelectorAll( `.${className}` )

        if ( ! selected.length ) return

        const defaultConfig = {
            parallaxSpeed : 1.1,
            parallaxLag   : 0.15
        }

        return Array.from( selected ).map(
            ( node, index ) => mixin( { node, config: { ...defaultConfig, ...config, className } } )
        )
    }
}
